import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuickGradingGrid = ({ data, onSaved }) => {
  const rows = data.rows.filter((r) => r.submission);
  const init = () => Object.fromEntries(rows.map((r) => [r.submission.id, { grade: r.submission.feedback?.grade ?? '', comment: r.submission.feedback?.comment || '' }]));
  const graded = rows.filter((r) => r.submission.feedback?.grade != null);
  const initCount = graded.length === 0 || graded.some((r) => r.submission.feedback.count_in_average);
  const [values, setValues] = useState(init);
  const [count, setCount] = useState(initCount);
  const [busy, setBusy] = useState(false);
  const max = data.assessment.max_grade || 20;
  useEffect(() => { setValues(init()); setCount(initCount); }, [data]); // re-sync after save

  const set = (id, k, v) => setValues((prev) => ({ ...prev, [id]: { ...prev[id], [k]: v } }));
  const invalid = Object.values(values).some((v) => v.grade !== '' && (Number(v.grade) < 0 || Number(v.grade) > max));

  const save = async () => {
    setBusy(true);
    try {
      const original = init();
      const items = Object.entries(values)
        .filter(([id, v]) => count !== initCount || String(v.grade) !== String(original[id].grade) || v.comment !== original[id].comment)
        .map(([submission_id, v]) => ({ submission_id, grade: v.grade === '' ? null : Number(v.grade), comment: v.comment || null }));
      if (items.length === 0) {
        toast.info('Aucune modification à enregistrer');
        return;
      }
      const { data: res } = await axios.post(`${API}/homework/${data.assessment.id}/grades/bulk`, { count_in_average: count, items });
      if (res.errors.length) toast.error(res.message); else toast.success(res.message);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  };

  if (rows.length === 0) return <p className="text-sm text-gray-500">Aucune copie rendue pour l’instant.</p>;
  return (
    <div className="space-y-3" data-testid="quick-grading-grid">
      <div className="overflow-x-auto border rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50"><tr>
            {['Élève', 'Rendu', `Note / ${max}`, 'Commentaire'].map((h) => <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y">
            {rows.map((r, i) => {
              const v = values[r.submission.id] || { grade: '', comment: '' };
              const bad = v.grade !== '' && (Number(v.grade) < 0 || Number(v.grade) > max);
              return (
                <tr key={r.student_id}>
                  <td className="px-3 py-2 font-medium">{r.student_name}</td>
                  <td className="px-3 py-2 text-gray-500">{r.status === 'LATE' ? 'En retard' : 'À temps'}</td>
                  <td className="px-3 py-2 w-28">
                    <Input type="number" min="0" max={max} step="0.25" value={v.grade} className={bad ? 'border-red-500' : ''}
                      onChange={(e) => set(r.submission.id, 'grade', e.target.value)} data-testid={`quick-grade-${i}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input value={v.comment} placeholder="Facultatif" onChange={(e) => set(r.submission.id, 'comment', e.target.value)} data-testid={`quick-comment-${i}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={count} onCheckedChange={setCount} data-testid="quick-count-switch" /> Les notes comptent dans la moyenne
        </label>
        <Button onClick={save} disabled={busy || invalid} data-testid="quick-grading-save-btn"><Save className="w-4 h-4 mr-2" /> Enregistrer toutes les corrections</Button>
      </div>
      {invalid && <p className="text-xs text-red-600">Une note dépasse le barème ({max}).</p>}
    </div>
  );
};

export default QuickGradingGrid;
